import { createRouter, createWebHistory } from "vue-router";
import { useStore } from "@/store/getStartStore.js";

// Lazy loading компонентів
const ProductMenu = () => import("@/pages/productMenu.vue");
const BasketPage = () => import("@/pages/basketPage.vue");
const SearchPage = () => import("@/pages/searchPage.vue");
const SavedPage = () => import("@/pages/savedPage.vue");
const LoaderPage = () => import("@/pages/loaderPage.vue");
const TermsPage = () => import("@/pages/termsPage.vue");

// Константи
const STORAGE_KEYS = {
  TPAYID: "tpayid",
  MENU_ID: "menuId",
  TRADEPOINT_ID: "tradepointId",
  PRODUCTS: "products",
  SAVED_PREFIX: "saved-",
};

const DEFAULT_TPAYID =
  process.env.VUE_APP_DEFAULT_TPAYID || "c710930f-0265-4fa0-b8ae-baf587b83e1b";

// Клас для управління історією маршрутів
class RouteHistoryManager {
  constructor(maxSize = 10) {
    this.history = [];
    this.maxSize = maxSize;
  }

  push(route) {
    if (this.history.length > 0) {
      const lastRoute = this.history[this.history.length - 1];
      if (
        lastRoute.path === route.path &&
        JSON.stringify(lastRoute.query) === JSON.stringify(route.query)
      ) {
        return;
      }
    }

    this.history.push({
      path: route.path,
      query: { ...route.query },
      params: { ...route.params },
      meta: { ...route.meta },
    });

    if (this.history.length > this.maxSize) {
      this.history.shift();
    }
  }

  getPrevious() {
    return this.history.length > 1
      ? this.history[this.history.length - 2]
      : null;
  }

  clear() {
    this.history = [];
  }

  getAll() {
    return [...this.history];
  }
}

const routeHistory = new RouteHistoryManager();

// Експорт функцій для історії
export function getPreviousRoute() {
  return routeHistory.getPrevious();
}

export function getRouteHistory() {
  return routeHistory.getAll();
}

// Визначення маршрутів
const routes = [
  {
    path: "/",
    name: "home",
    component: ProductMenu,
    meta: {
      title: "Меню",
      requiresInit: true,
    },
  },
  {
    path: "/:id?",
    name: "category",
    component: ProductMenu,
    props: true,
    meta: {
      title: "Категорія",
      requiresInit: true,
    },
  },
  {
    path: "/search",
    name: "search",
    component: SearchPage,
    meta: {
      title: "Пошук",
      requiresInit: true,
    },
  },
  {
    path: "/saved",
    name: "saved",
    component: SavedPage,
    meta: {
      title: "Збережені",
      requiresSaved: true,
      requiresInit: true,
    },
  },
  {
    path: "/basket",
    name: "basketPage",
    component: BasketPage,
    meta: {
      title: "Кошик",
      requiresInit: true,
    },
  },
  {
    path: "/loader",
    name: "loaderPage",
    component: LoaderPage,
    meta: {
      title: "Завантаження",
      skipInit: true,
    },
  },
  {
    // Публічна оферта закладу — вимога LiqPay до сайту мерчанта, п.3.
    // requiresInit, бо реквізити продавця беруться з getStart.
    path: "/terms",
    name: "terms",
    component: TermsPage,
    meta: {
      title: "Умови користування",
      requiresInit: true,
    },
  },
];

// Створення роутера
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    } else {
      return { top: 0, behavior: "smooth" };
    }
  },
});

// Допоміжний клас для очищення localStorage
const StorageHelper = {
  clearConflictingData(keepTpayid = false) {
    try {
      if (keepTpayid) {
        localStorage.removeItem(STORAGE_KEYS.MENU_ID);
        localStorage.removeItem(STORAGE_KEYS.TRADEPOINT_ID);
      } else {
        localStorage.removeItem(STORAGE_KEYS.TPAYID);
      }
    } catch (e) {
      console.error("Failed to clear storage:", e);
    }
  },

  resetBasket() {
    try {
      localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify([]));
    } catch (e) {
      console.error("Failed to reset basket:", e);
    }
  },
};

// Головний navigation guard
router.beforeEach(async (to, from, next) => {
  const store = useStore();

  // Зберігаємо історію навігації
  if (from.path && from.path !== "/") {
    routeHistory.push(from);
  }

  // Встановлюємо title сторінки
  if (to.meta.title) {
    const companyName = store.startSettings?.companyName || "Меню";
    document.title = `${to.meta.title} - ${companyName}`;
  }

  // Пропускаємо ініціалізацію для певних сторінок
  if (to.meta.skipInit) {
    return next();
  }

  // Перевірка для сторінки "Saved"
  if (to.meta.requiresSaved) {
    const companyId = store.startSettings?.companyId;

    if (!companyId) {
      // Якщо немає companyId, спробуємо завантажити налаштування
      await store.loadStartSettings();
    }

    const savedKey = `${STORAGE_KEYS.SAVED_PREFIX}${store.startSettings?.companyId}`;
    const savedRaw = localStorage.getItem(savedKey);
    const saved = savedRaw ? JSON.parse(savedRaw) : [];

    if (!Array.isArray(saved) || saved.length === 0) {
      return next("/");
    }
  }

  try {
    const { tpayid, menuId, tradepointId } = to.query;

    // Логіка ініціалізації на основі параметрів
    if (tpayid) {
      // Режим tpayid
      if (store.tpayid !== tpayid) {
        StorageHelper.clearConflictingData(true);
        store.setTpayid(tpayid);
        store.basketDishes = [];
        StorageHelper.resetBasket();
        await store.loadStartSettings();
      } else if (
        !store.startSettings ||
        Object.keys(store.startSettings).length === 0
      ) {
        await store.loadStartSettings();
      }
      return next();
    } else if (menuId) {
      // Режим menuId
      if (store.menuId !== menuId) {
        StorageHelper.clearConflictingData(false);
        store.setmenuId(menuId);
        if (tradepointId) {
          store.setTradepointid(tradepointId);
        }
        await store.loadStartSettings();
      } else if (
        !store.startSettings ||
        Object.keys(store.startSettings).length === 0
      ) {
        await store.loadStartSettings();
      }
      return next();
    } else if (store.menuId) {
      // Якщо вже є menuId в store
      return next({
        path: to.path,
        params: to.params,
        query: {
          ...to.query,
          menuId: store.menuId,
          ...(store.tradepointId && { tradepointId: store.tradepointId }),
        },
      });
    } else if (store.tpayid) {
      // Якщо вже є tpayid в store
      return next({
        path: to.path,
        params: to.params,
        query: {
          ...to.query,
          tpayid: store.tpayid,
        },
      });
    } else {
      // Значення за замовчуванням
      store.setTpayid(DEFAULT_TPAYID);
      await store.loadStartSettings();
      return next({
        path: to.path,
        params: to.params,
        query: {
          ...to.query,
          tpayid: DEFAULT_TPAYID,
        },
      });
    }
  } catch (error) {
    console.error("Navigation error:", error);
    store.loading = false;

    // При критичній помилці переходимо на loader
    if (to.name !== "loaderPage") {
      next({ name: "loaderPage" });
    } else {
      next(false);
    }
  }
});

// After navigation hook
router.afterEach((to) => {
  // Приховуємо loader якщо він був показаний
  const store = useStore();
  if (store.loading && to.name !== "loaderPage") {
    store.loading = false;
  }
});

export default router;
